import { DateTime } from "luxon";
import { Frequency, RRule, RRuleSet } from "rrule";
import { time_format_utils } from "./time_format_utils";

/**
 * True if the RRULE repeats more frequently than the dashboard UI allows.
 * The repeat picker's most granular option is hourly, so anything sub-hourly
 * (MINUTELY / SECONDLY) can only arrive by bypassing the client (crafted
 * request, public API, etc.) and must be rejected server-side.
 *
 * Returns false for empty/unparseable strings — those are handled by the
 * normal RRULE parsing/validation elsewhere.
 */
export function isRRuleTooFrequent(rrule_str: string): boolean {
	if (!rrule_str) {
		return false;
	}

	let freq: Frequency | undefined;
	try {
		freq = RRule.parseString(rrule_str).freq;
	} catch {
		return false;
	}

	// Frequency enum is ordered coarse→fine: YEARLY(0) … HOURLY(4), MINUTELY(5), SECONDLY(6).
	return freq !== undefined && freq > Frequency.HOURLY;
}

export type RDateDescriptor = {
	/** The original date of the repeat occurrence that should be excluded in the future */
	orig_date: Date | string;
	/** The new date of the repeat occurrence that should be excluded in the future */
	rdate: Date | string;
};

export function getNextNOccurrences(
	rrule: RRuleSet,
	start_time: Date,
	repeat_tz: string,
	n: number,
	include = true
) {
	const current_start_time = timeToRRuleTime(start_time, repeat_tz);

	return rrule.between(
		current_start_time,
		new Date(2050, 1, 1),
		include,
		(date, len) => {
			return len < n;
		}
	);
}

export function getPrevious(
	rrule: RRuleSet,
	repeat_start: Date,
	current_time: Date
) {
	let i = 0;
	let next_start_time: DateTime = DateTime.fromJSDate(repeat_start);
	let previous_start_time: DateTime | null = null;
	while (
		next_start_time &&
		next_start_time.toMillis() < current_time.getTime()
	) {
		const candidate = rruleAfter(rrule, next_start_time.toJSDate());

		if (!candidate) {
			break;
		}
		previous_start_time = next_start_time;
		next_start_time = candidate;

		if (i > 1000) {
			break;
		}
		i++;
	}
	return previous_start_time?.toUTC();
}

export function isTimeBetweenRepeats(
	rrule: RRuleSet,
	repeat_start: Date,
	current_time: Date,
	desired_time: Date,
	duration_ms: number
) {
	const previous_time = getPrevious(rrule, repeat_start, current_time);

	const subsequent_time = rruleAfter(
		rrule,
		DateTime.fromJSDate(current_time)
			.toUTC(undefined, { keepLocalTime: true })
			.toJSDate()
	);

	const is_after_previous =
		desired_time.getTime() > Date.now() &&
		(!previous_time ||
			previous_time.toMillis() + duration_ms < desired_time.getTime());
	const is_before_subsequent =
		!subsequent_time ||
		subsequent_time.toMillis() > desired_time.getTime() + duration_ms;

	// console.log();
	// console.log("previous", previous_time?.toISO());
	// console.log("current_time", current_time.toISOString());
	// console.log("subsequent", subsequent_time?.toISO());
	// console.log("desired_time", desired_time.toISOString());

	return is_after_previous && is_before_subsequent;
}

export function getPreviousNOccurrences(
	rrule: RRuleSet,
	raw_event_start_time: Date,
	raw_current_start_time: Date,
	repeat_tz: string,
	n: number
) {
	const event_start_time = timeToRRuleTime(raw_event_start_time, repeat_tz);

	const dates: Date[] = [];
	let current_date: Date | null = raw_current_start_time;
	for (let i = 0; i < n; i++) {
		current_date = rrule.before(
			timeToRRuleTime(current_date, repeat_tz),
			false
		);

		if (
			!current_date ||
			current_date.getTime() < event_start_time.getTime()
		) {
			break;
		}

		dates.push(current_date);
	}

	return dates.reverse();
}

export function timeToRRuleTime(start_time: Date, repeat_tz: string) {
	return DateTime.fromJSDate(start_time)
		.setZone(repeat_tz)
		.toUTC(undefined, { keepLocalTime: true })
		.toJSDate();
}

export function rruleTimeToLocalTime(rrule_date: Date) {
	return DateTime.fromJSDate(rrule_date)
		.toUTC()
		.setZone("local", { keepLocalTime: true });
}

export function rruleAfter(rrule_set: RRuleSet, start: Date) {
	const start_datetime = DateTime.fromJSDate(start);

	// TODO repeat: test calendar page with different system timezone

	const next_date_result = rrule_set.after(start_datetime.toJSDate(), false);

	if (!next_date_result) {
		return null;
	}

	return rruleTimeToLocalTime(next_date_result).toUTC();
}

export function createRRuleSet({
	rrule_str,
	start_time_js,
	repeat_tz,
	exdates,
	rdates,
}: {
	rrule_str: string;
	start_time_js: Date;
	repeat_tz: string;
	exdates?: Array<string | Date> | null;
	rdates: Array<RDateDescriptor> | null;
}) {
	const start_time = DateTime.fromJSDate(start_time_js).toUTC();

	const rrule_set = new RRuleSet();
	const rrule = new RRule({
		...time_format_utils.transformRRuleToTimezone(
			RRule.parseString(rrule_str),
			repeat_tz,
			start_time,
			true
		),
		dtstart: timeToRRuleTime(start_time.toJSDate(), repeat_tz),
		tzid: repeat_tz === "UTC" ? "GMT" : repeat_tz,
	});

	if (rrule.options.until) {
		// rrule.options.until = DateTime.fromJSDate(rrule.options.until)
		// 	.setZone(local_timezone)
		// 	.toUTC(undefined, { keepLocalTime: true })
		// 	.toJSDate();

		rrule.options.until = timeToRRuleTime(rrule.options.until, repeat_tz);
	}

	rrule_set.rrule(rrule);
	exdates?.forEach((exdate) =>
		rrule_set.exdate(timeToRRuleTime(new Date(exdate), repeat_tz))
	);
	rdates?.forEach((rdate) => {
		rrule_set.exdate(timeToRRuleTime(new Date(rdate.orig_date), repeat_tz));
		rrule_set.rdate(timeToRRuleTime(new Date(rdate.rdate), repeat_tz));
	});

	return rrule_set;
}
